'use client'

import { Input } from '@headlessui/react'
import clsx from 'clsx'
import type { ControlledFormField, FormFieldCommonProps } from 'lib/types/Form'
import { ChangeEvent, forwardRef, useCallback, useState } from 'react'
import { Controller } from 'react-hook-form'
import Icon from '../../Icon/Icon'
import InputAddon, { InputAddonProps } from '../InputAddon/InputAddon'
import InputError from '../InputError/InputError'
import InputHint from '../InputHint/InputHint'
import InputLabel from '../InputLabel/InputLabel'

interface PasswordInputStandaloneProps
  extends InputAddonProps,
    Omit<FormFieldCommonProps, 'onChange'> {
  /**
   * Placeholder text for the input. Shown when the input is empty.
   */
  placeholder?: string
  /**
   * Optional autocomplete attribute for the input
   */
  autoComplete?: string
  /**
   * Optional callback that runs when the input value changes
   */
  onChange?: (data?: string | number, fieldName?: string) => void
}

interface PasswordInputProps
  extends ControlledFormField,
    PasswordInputStandaloneProps {}

// Standalone component that can be used outside react-hook-form's context
export const PasswordInputStandalone = forwardRef<
  HTMLInputElement,
  PasswordInputStandaloneProps
>(function PasswordInputStandaloneWithRef(
  {
    label,
    name,
    id = name,
    className,
    error,
    onChange,
    defaultValue,
    autoComplete,
    disabled,
    placeholder,
    hint,
    ...props
  }: PasswordInputStandaloneProps,
  ref,
) {
  const [isMasked, setIsMasked] = useState(true)
  const [isEnabled, setIsEnabled] = useState(
    'conditionalEdit' in props && props.conditionalEdit ? false : !disabled,
  )
  const [currentValue, setCurrentValue] = useState(defaultValue || '')
  const onInputChange = useCallback(
    ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
      setCurrentValue(value)
      onChange?.(value, name)
    },
    [onChange, name],
  )

  return (
    <div className={clsx(`text-input`, className)}>
      <div className="flex items-baseline justify-between">
        {label && <InputLabel htmlFor={id}>{label}</InputLabel>}
        <InputAddon
          {...props}
          {...('conditionalEdit' in props && props.conditionalEdit
            ? { onEdit: setIsEnabled }
            : {})}
        />
      </div>
      <div className="relative w-full">
        <Input
          className={clsx(
            'w-full type-body-8 rounded-8 h-12 flex items-center px-4 my-2 transition-shadow lg:h-14 mb-0',
            'disabled:bg-neutral-200 text-neutral-700 disabled:text-neutral-600 disabled:shadow-input-disabled disabled:cursor-not-allowed',
            'focus:shadow-input-focus focus:outline-none',
            'hover:shadow-input-active',
            error ? '!shadow-input-error' : 'shadow-input',
          )}
          type={isMasked ? 'password' : 'text'}
          disabled={!isEnabled}
          name={name}
          autoComplete={autoComplete}
          id={id}
          value={currentValue}
          placeholder={placeholder}
          ref={ref}
          onChange={onInputChange}
          data-dd-privacy="mask"
        />
        <button
          type="button"
          onClick={() => setIsMasked(!isMasked)}
          className="absolute top-1/2 -translate-y-1/2 right-4 w-4 h-4 md:w-6 md:h-6"
          aria-label={`click to ${isMasked ? 'show' : 'hide'} passowrd`}
        >
          <Icon
            icon={!isMasked ? 'visibility' : 'visibility-off'}
            className="w-full h-full"
          />
        </button>
      </div>
      <InputHint>{hint}</InputHint>
      <InputError>{error}</InputError>
    </div>
  )
})

// Controlled form element that is automatically registered to react-hook-form
export default function PasswordInput({
  name,
  control,
  rules,
  defaultValue,
  onChange,
  disabled,
  ...props
}: PasswordInputProps) {
  return (
    <Controller
      name={name || ''}
      defaultValue={defaultValue}
      control={control}
      rules={rules}
      render={({
        field: { onChange: fieldOnChange, ...field },
        fieldState,
      }) => {
        const onInputChange = (data?: number | string) => {
          fieldOnChange?.(data)
          onChange?.(data, name)
        }
        return (
          <PasswordInputStandalone
            {...props}
            {...field}
            defaultValue={defaultValue}
            disabled={disabled}
            onChange={onInputChange}
            error={fieldState.error?.message || props.error}
          />
        )
      }}
    />
  )
}
